import { JAM_CHALLENGE_TASK_ROUTES } from '@/src/routes';
import { useFlashbars } from '@/src/store/flashbar.context';
import { useJamChallengeDetails } from '@/src/store/jam-challenge-details.context';
import { useJamEventDetails } from '@/src/store/jam-event-details.context';
import { JamTask, JamTaskStatus } from '@/src/types/JamChallengeDetails';
import { onKeyboardActivation } from '@/src/utils/a11y.utils';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { Box, ColumnLayout, Container, SpaceBetween } from '@amzn/awsui-components-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import TaskStatus from '../JamTask/TaskStatus';
import styles from './TaskDetails.module.scss';

const TaskDetails: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { eventName, event } = useJamEventDetails();
  const { selectedJamChallenge, isSelectedChallengeStarted, jamChallengeId } = useJamChallengeDetails();
  const { addErrorFlashbar } = useFlashbars();
  const [selectedTask, setSelectedTask] = useState<JamTask>();
  const TotalEarnedPoints: number = selectedJamChallenge?.score || 0;
  const TotalPoints: number = selectedJamChallenge?.possiblePoints || 0;

  // Drives both nav gating and the lock icon
  const challengeStarted = isSelectedChallengeStarted;

  const handleNavigation = (task: JamTask) => {
    // Facilitators may navigate tasks of a not-started challenge; participants cannot.
    if (!challengeStarted && !event?.facilitator) {
      addErrorFlashbar(t(i18nKeys.myJams.challenges.errors.startJamChallenge));
      return;
    }
    // Select only when navigation is permitted - disallowed task doesn't get the selected highlight.
    if (!task.locked) {
      setSelectedTask(task);
      history.push(JAM_CHALLENGE_TASK_ROUTES.resolve([eventName, jamChallengeId, task.id]));
    }
  };

  return (
    <SpaceBetween direction="vertical" size="l">
      <ColumnLayout columns={2}>
        {Array.isArray(selectedJamChallenge?.tasks) &&
          selectedJamChallenge?.tasks
            ?.sort((a, b) => (a.taskNumber > b.taskNumber ? 1 : a.taskNumber < b.taskNumber ? -1 : 0))
            .map((task, index) => (
              <div
                key={task.id}
                id={`task${index + 1}`}
                data-testid={`task${index + 1}`}
                onClick={() => handleNavigation(task)}
                onKeyDown={(e) => onKeyboardActivation(e, () => handleNavigation(task))}
                tabIndex={0}
                role="button"
                className={styles.taskContainer}
                aria-label={task.title}
              >
                <Container className={`task-card-container dark-bg ${selectedTask?.id === task.id ? 'solved' : ''}`}>
                  <Box padding={'xs'}>
                    <SpaceBetween direction="vertical" size="m">
                      <Box variant="div">
                        {t(i18nKeys.myJams.challenges.details.task.title)} {task?.taskNumber}
                        <Box variant="span" margin={{ left: 's' }}>
                          <TaskStatus
                            taskCompleted={task.status === JamTaskStatus.COMPLETE}
                            taskLocked={task.locked}
                            status={event?.eventStatus}
                            challengeStatus={challengeStarted}
                          />
                        </Box>
                      </Box>
                      <Box variant="h2" color="text-status-info">
                        {task.title ?? t(i18nKeys.myJams.challenges.details.task.noTitle)}
                      </Box>
                      <ColumnLayout borders="horizontal" columns={2} className="task-card-container">
                        <Box>{t(i18nKeys.myJams.challenges.details.task.status)}</Box>
                        <div className={styles.infoValue}>{task.status}</div>
                        <Box>{t(i18nKeys.myJams.challenges.details.task.possiblePoints)}</Box>
                        <div className={styles.infoValue}>{task.pointsPossible}</div>
                        <Box>{t(i18nKeys.myJams.challenges.details.task.clueDeductionPoints)}</Box>
                        <div className={styles.infoValue}>{task.cluesPenalty}</div>
                        <Box>{t(i18nKeys.myJams.challenges.details.task.totalEarned)}</Box>
                        <div className={styles.infoValue}>{task.cluesPenalty < 0 ? '--' : task.score}</div>
                      </ColumnLayout>
                    </SpaceBetween>
                  </Box>
                </Container>
              </div>
            ))}
      </ColumnLayout>
      <Box margin={{ bottom: 'xl' }}>
        {selectedJamChallenge?.tasks && selectedJamChallenge?.tasks.length > 0 && (
          <Container className="dark-bg">
            <Box variant="h2" textAlign="center">
              {t(i18nKeys.myJams.challenges.details.task.totalPointsEarned)}:
              <span className={styles.totalPoints}>
                {TotalEarnedPoints}/{TotalPoints}
              </span>
            </Box>
          </Container>
        )}
      </Box>
    </SpaceBetween>
  );
};

export default TaskDetails;
